import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { StoryblokReplaceService } from '../../../services/storyblok-replace.service';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { BlockStyle } from '../../interfaces/block-style';
import { convertToStoryblokCdn } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokDirective } from '../../storyblok.directive';
import { PICFLOW_CONFIG } from '../../../picflow-config';

export const getPageWidth = (pageWidth: string, customWidth?: number | string) => {
  if (pageWidth === 'hug' || pageWidth === 'full') {
    return undefined;
  }
  const custom = Number(customWidth);
  if (customWidth != null && customWidth !== '' && Number.isFinite(custom) && custom > 0) {
    return custom;
  }
  if (pageWidth === '4col') {
    return 380;
  }
  if (pageWidth === '5col') {
    return 480;
  }
  if (pageWidth === '6col') {
    return 580;
  }
  if (pageWidth === '7col') {
    return 680;
  }
  if (pageWidth === '8col') {
    return 780;
  }
  if (pageWidth === '9col') {
    return 880;
  }
  if (pageWidth === '10col') {
    return 980;
  }
  if (pageWidth === '12col' || !pageWidth) {
    return 1180;
  }
  if (pageWidth === '14col') {
    return 1380;
  }
};

@Component({
  selector: 'app-block-wrapper',
  imports: [StoryblokDirective, ResizeDirective],
  templateUrl: './block-wrapper.component.html',
  styleUrls: ['./block-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': '"section " + baseStyleClass() + " " + otherClasses()',
    '[class.theme-dark]': 'style().theme === "dark"',
    '[class.theme-light]': 'style().theme === "light"',
    '[class.no-horizontal-padding]': 'style().noHorizontalPadding || style().inWrapper',
    '[class.in-wrapper]': 'style().inWrapper',
    '[style.background-color]': 'backgroundColor()',
    '[style.background-image]': 'backgroundImage()',
    '[style.background-size]': 'backgroundSize()',
    '[style.background-position]': 'backgroundPosition()',
    '[style.background-repeat]': 'backgroundRepeat()',
  },
})
export class BlockWrapperComponent {
  private storyblokReplaceService = inject(StoryblokReplaceService);
  private config = inject(PICFLOW_CONFIG);
  readonly _editable = input<string>(undefined);
  readonly otherClasses = input<string>(undefined);
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly _style = input<BlockStyle[]>(undefined, { alias: 'style' });

  readonly style = computed(() => {
    const defaultStyle = {
      theme: 'inherit' as const,
      topSpacing: '4',
      bottomSpacing: '4',
      pageWidth: '12col',
    };

    return {
      ...defaultStyle,
      ...(this._style()?.[0] || {}),
    };
  });

  readonly screens = computed(() => {
    const screens = ['mobile', 'tablet', 'desktop'];
    if (this.style().hideOnMobile) screens.splice(screens.indexOf('mobile'), 1);
    return screens as ('mobile' | 'tablet' | 'desktop')[];
  });
  readonly isHug = computed(() => this.style().pageWidth === 'hug');

  readonly pageWidth = computed(() => {
    const style = this.style();
    return getPageWidth(style.pageWidth, style.customPageWidth);
  });

  readonly maxPageWidthCss = computed(() => {
    if (this.isHug()) return null;
    const px = this.pageWidth();
    if (px) return `${px}px`;
    return '100%';
  });

  readonly maxPageWidthMobileCss = computed(() => {
    if (this.isHug()) return null;
    const raw = this.style().customPageWidthMobile as number | string | null | undefined;
    const mobile = Number(raw);
    if (raw != null && String(raw) !== '' && Number.isFinite(mobile) && mobile > 0) {
      return `${mobile}px`;
    }
    return this.maxPageWidthCss();
  });

  readonly customHidden = computed(() => {
    const style = this.style();
    return style.customTemplateCondition && !this.storyblokReplaceService.getFromKey(style.customTemplateCondition);
  });

  /**
   * Theme-aware base backgrounds (--layout-base-*) via block_style `style`.
   * Unset = transparent (nested bloks inside heroes must not paint boxes).
   */
  readonly baseStyleClass = computed(() => {
    const s = this.style();
    return s.style ? `bg-${s.style}` : '';
  });

  readonly backgroundColor = computed(() => this.style().customColor || undefined);
  readonly backgroundImage = computed(() => {
    const style = this.style();
    if (style.backgroundGradient) return style.backgroundGradient;

    const imageUrl = style.backgroundImage?.filename;
    if (!imageUrl) return undefined;
    const url = convertToStoryblokCdn(imageUrl, this.config.storyblokUrl).replace(/"/g, '%22');
    return `url("${url}")`;
  });
  readonly usesBackgroundImage = computed(
    () => !!this.style().backgroundImage?.filename && !this.style().backgroundGradient
  );
  readonly backgroundSize = computed(() => (this.usesBackgroundImage() ? 'cover' : undefined));
  readonly backgroundPosition = computed(() => (this.usesBackgroundImage() ? 'center' : undefined));
  readonly backgroundRepeat = computed(() => (this.usesBackgroundImage() ? 'no-repeat' : undefined));
}
